import { askConfirm } from '@/components/ui/ConfirmHost';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCbtAttempt, getCbtExams, getCbtExamsById, getCbtExamsResults, postCbtAttemptsGrade, postCbtExamsRelease } from '@/api/teacher';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Banner, Modal, PageHeader, QueryState, Spinner, Stat, Table } from '@/components/kit/ui';
import { asList, errMsg, errStatus, unwrap } from '@/components/kit/data';
import { ExamSelect, type ExamRow } from '../shared';

interface AttemptRow { attempt_id: number; student_id: number; name: string; admission_no: string; attempt_no: number; status: string; score: number | null; percent: number | null; passed: boolean | null; needs_marking: number; flagged: boolean; counts: boolean }
interface Results { exam: string; total_marks: number; pass_mark: number | null; status: string; stats: { students: number; average: number; highest: number; lowest: number; passed: number | null }; attempts: AttemptRow[] }
interface AttemptAnswer { question_id: number; type: string; body: string; marks: number; answer: unknown; score: number | null; is_correct: boolean | null; needs_marking?: boolean }
interface AttemptDetail { answers: AttemptAnswer[] }
interface ExamDetail { question_list: { id: number; type: string; body: string; marks: number }[] }

const MANUAL = ['essay', 'short_answer', 'short', 'text', 'fill_blank'];

function GradeDialog({ examId, row, onClose, onSaved }: { examId: string; row: AttemptRow; onClose: () => void; onSaved: (t: string) => void }) {
  const detail = useQuery({ queryKey: ['teacher-cbt-attempt', row.attempt_id], queryFn: () => getCbtAttempt(row.attempt_id).then((r) => unwrap<AttemptDetail>(r)), retry: false });
  const fallback = useQuery({ queryKey: ['teacher-cbt-exam', examId], enabled: detail.isError && errStatus(detail.error) === 404, queryFn: () => getCbtExamsById(examId).then((r) => unwrap<ExamDetail>(r)) });
  const items: AttemptAnswer[] = detail.data?.answers ?? (fallback.data?.question_list ?? []).map((q) => ({ question_id: q.id, type: q.type, body: q.body, marks: q.marks, answer: undefined, score: null, is_correct: null }));
  const manual = items.filter((a) => a.needs_marking || MANUAL.includes(a.type));
  const [marks, setMarks] = useState<Record<number, string>>({});
  const save = useMutation({
    mutationFn: () => postCbtAttemptsGrade(row.attempt_id, { answers: Object.entries(marks).filter(([, v]) => v !== '').map(([q, v]) => ({ question_id: Number(q), score: Number(v) })) }),
    onSuccess: (r) => onSaved(`Marks saved for ${row.name}. New score: ${unwrap<{ score: number }>(r).score}.`),
  });
  const bad = manual.some((a) => marks[a.question_id] !== undefined && marks[a.question_id] !== '' && (Number(marks[a.question_id]) < 0 || Number(marks[a.question_id]) > a.marks));
  const loading = detail.isLoading || fallback.isLoading;
  return (
    <Modal title={`Mark ${row.name}`} onClose={onClose} wide>
      {loading ? <Spinner /> : detail.isError && !fallback.data ? <Banner>{errMsg(detail.error)}</Banner> : !manual.length ? <p className="text-sm text-slate-500">This attempt has no answers that need hand marking.</p> : (
        <div className="space-y-3">
          {!detail.data && <Banner tone="warn">The student's written answers cannot be loaded from the server yet; enter marks from the paper or the student's device.</Banner>}
          {manual.map((a) => (
            <div key={a.question_id} className="border rounded-xl p-3 text-sm space-y-2">
              <div className="font-medium whitespace-pre-wrap">{a.body}</div>
              {a.answer !== undefined && <div className="bg-slate-50 rounded-lg p-2 whitespace-pre-wrap">{a.answer === null || a.answer === '' ? <i className="text-slate-400">No answer</i> : String(a.answer)}</div>}
              <label className="flex items-center gap-2">Score <input inputMode="decimal" className="w-20 border rounded-lg px-2 py-1 text-right" value={marks[a.question_id] ?? (a.score ?? '').toString()} onChange={(e) => setMarks((p) => ({ ...p, [a.question_id]: e.target.value.replace(/[^\d.]/g, '') }))} /> / {a.marks}</label>
            </div>
          ))}
          {save.isError && <Banner>{errMsg(save.error)}</Banner>}
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={bad || save.isPending || !Object.values(marks).some((v) => v !== '')} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save marks'}</Button></div>
        </div>
      )}
    </Modal>
  );
}

export function CbtResultsPage() {
  const qc = useQueryClient();
  const [id, setId] = useState('');
  const [grading, setGrading] = useState<AttemptRow | null>(null);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const exams = useQuery({ queryKey: ['teacher-cbt-exams'], queryFn: () => getCbtExams({ per_page: 100 }).then((r) => asList<ExamRow>(unwrap(r))) });
  const q = useQuery({ queryKey: ['teacher-cbt-results', id], enabled: !!id, queryFn: () => getCbtExamsResults(id).then((r) => unwrap<Results>(r)) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['teacher-cbt-results', id] }); qc.invalidateQueries({ queryKey: ['teacher-cbt-exams'] }); };
  const release = useMutation({
    mutationFn: (force: boolean) => postCbtExamsRelease(id, force ? { force: true } : {}),
    onSuccess: (r) => { setMsg({ tone: 'success', text: (r.data as { message?: string }).message ?? 'Released.' }); refresh(); },
    onError: (e, force) => {
      const text = errMsg(e);
      if (!force && errStatus(e) === 422 && /force|still|marking/i.test(text)) void askConfirm(`${text}\n\nRelease anyway? Students still writing will be submitted now.`, { title: 'Release anyway?', confirmLabel: 'Release now' }).then((ok) => { if (ok) release.mutate(true); });
      else setMsg({ tone: 'error', text });
    },
  });
  const d = q.data;

  return (
    <div className="space-y-4">
      <PageHeader title="CBT results" subtitle="Scores per attempt, hand-mark essays and release results to students." />
      {msg && <Banner tone={msg.tone}>{msg.text}</Banner>}
      <Card>
        <QueryState q={exams}>
          <div className="flex gap-3 items-end flex-wrap">
            <div className="w-full max-w-md"><ExamSelect exams={exams.data ?? []} value={id} onChange={setId} /></div>
            {d && d.status !== 'released' && <Button disabled={release.isPending} onClick={async () => { if (await askConfirm('It closes the test, shows students their results and sends scores to the linked result column.', { title: 'Release this test?', confirmLabel: 'Release results' })) release.mutate(false); }}>{release.isPending ? 'Releasing…' : 'Release results'}</Button>}
            {d?.status === 'released' && <StatusBadge status="released" />}
          </div>
        </QueryState>
      </Card>
      {id && (
        <QueryState q={q}>
          {d && (
            <>
              <div className="grid sm:grid-cols-5 gap-3">
                <Stat label="Students" value={d.stats.students} />
                <Stat label="Average" value={`${d.stats.average} / ${d.total_marks}`} />
                <Stat label="Highest" value={d.stats.highest} />
                <Stat label="Lowest" value={d.stats.lowest} />
                <Stat label="Passed" value={d.stats.passed ?? '—'} />
              </div>
              <Card>
                <Table rows={d.attempts} rowKey={(r) => r.attempt_id} empty="No attempts yet." cols={[
                  { key: 'name', header: 'Student', render: (r) => <>{r.name}<span className="block text-xs text-slate-400">{r.admission_no} · attempt {r.attempt_no}{r.counts ? ' · counts' : ''}</span></> },
                  { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
                  { key: 'score', header: 'Score', render: (r) => (r.score !== null ? `${r.score} (${r.percent ?? 0}%)` : '—') },
                  { key: 'passed', header: 'Pass', render: (r) => (r.passed === null ? '—' : r.passed ? 'Yes' : 'No') },
                  { key: 'flags', header: '', render: (r) => <span className="text-xs">{r.flagged ? <span className="text-red-700">flagged </span> : null}{r.needs_marking ? <span className="text-amber-700">{r.needs_marking} to mark</span> : null}</span> },
                  { key: 'a', header: '', render: (r) => !['in_progress', 'voided'].includes(r.status) ? <Button variant="ghost" onClick={() => setGrading(r)}>{r.needs_marking ? 'Mark' : 'Adjust marks'}</Button> : null },
                ]} />
              </Card>
            </>
          )}
        </QueryState>
      )}
      {grading && <GradeDialog examId={id} row={grading} onClose={() => setGrading(null)} onSaved={(t) => { setGrading(null); setMsg({ tone: 'success', text: t }); refresh(); }} />}
    </div>
  );
}
